import React, { useCallback, useEffect, useState } from 'react';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import Typography from 'Components/Atoms/Typography';
import { useTranslation } from 'react-i18next';
import useAuth from 'CustomHooks/useAuth';
import useRestaurant from 'CustomHooks/useRestaurant';
import useStillCurrent from 'CustomHooks/useStillCurrent';
import { listPaymentRequests, PaymentRequest } from 'api/paymentRequestsApi';
import { Reservation } from 'types/reservations';
import CreatePaymentRequestModal from './CreatePaymentRequestModal';
import PaymentRequestCard from './PaymentRequestCard';
import { euro } from './format';
import './styles.scss';

const CANCELLED = ['failed', 'negative'];

/**
 * Payment links at an existing reservation (menu deposit, a gift, …). Each
 * request is independent; not paying never changes the reservation.
 */
const ReservationPaymentRequests: React.FC<{ reservation: Reservation }> = ({ reservation }) => {
  const { t } = useTranslation('reservations');
  const { roleMap } = useAuth();
  const { apps } = useRestaurant();
  const appEnabled = apps.includes('payment_requests');
  const restaurantId = reservation.restaurant;
  const rights = {
    manage: !!(roleMap?.admin || roleMap?.chefWaiter),
    refund: !!roleMap?.admin,
  };
  const [requests, setRequests] = useState<PaymentRequest[] | null>(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const stillShown = useStillCurrent(`${restaurantId}/${reservation.id}`);

  const load = useCallback(async () => {
    const requestedFor = `${restaurantId}/${reservation.id}`;
    setError('');
    try {
      const list = await listPaymentRequests(restaurantId, reservation.id);
      if (stillShown(requestedFor)) setRequests(list);
    } catch (err: any) {
      if (stillShown(requestedFor)) setError(err.message);
    }
  }, [restaurantId, reservation.id, stillShown]);

  useEffect(() => {
    setRequests(null);
    load();
  }, [load]);

  const replace = (updated: PaymentRequest) =>
    setRequests((current) => {
      const list = current ?? [];
      return list.some((entry) => entry.id === updated.id)
        ? list.map((entry) => (entry.id === updated.id ? updated : entry))
        : [...list, updated];
    });

  const paid = (requests ?? []).reduce((sum, request) => sum + request.paidCents - request.refundedCents, 0);
  // What the team has not yet confirmed as deducted on the final bill; an
  // older marker without a POS receipt is unverified and credits nothing.
  const uncredited = (requests ?? [])
    .filter((request) => request.kind === 'deposit' && !request.credit?.posReference)
    .reduce((sum, request) => sum + (request.creditableCents ?? 0), 0);
  const cancelled = CANCELLED.includes(reservation.status as string);

  if (!appEnabled && !requests?.length && !error) return null;

  return (
    <Box className="payment-request-section">
      <Box className="payment-request-section__heading">
        <Typography variant="text-3" weight="bold" translation={null}>
          {t('Payment links')}
        </Typography>
        {appEnabled && rights.manage && !cancelled && (
          <Button variant="outlined" translation={null} aria-label={t('Create payment link')} onClick={() => setCreating(true)}>
            {t('Create payment link short')}
          </Button>
        )}
      </Box>
      {paid > 0 && (
        <dl className="payment-request-balances">
          <div><dt>{t('Paid')}</dt><dd>{euro(paid)}</dd></div>
          {uncredited > 0 && <div><dt title={t('Deposit to credit on the final bill')}>{t('Remaining credit on final bill')}</dt><dd>{euro(uncredited)}</dd></div>}
        </dl>
      )}
      {error && (
        <Typography color="error" block translation={null}>
          <span role="alert">{error}</span>
        </Typography>
      )}
      {requests === null && !error && <Typography variant="text-3" color="subdued" translation="reservations" role="status">Loading payment links…</Typography>}
      {requests?.length === 0 && <Typography variant="text-3" color="subdued" translation="reservations" block>No payment links yet. Create one for a deposit or another payment.</Typography>}
      {error && <Button variant="outlined" translation="reservations" onClick={load}>Try again</Button>}
      {requests?.map((request) => (
        <PaymentRequestCard
          key={request.id}
          restaurantId={restaurantId}
          request={request}
          rights={rights}
          onChange={replace}
        />
      ))}
      {creating && appEnabled && (
        <CreatePaymentRequestModal
          reservation={reservation}
          onClose={() => setCreating(false)}
          onCreated={(request) => {
            setCreating(false);
            replace(request);
          }}
        />
      )}
    </Box>
  );
};

/**
 * One state scope per reservation: the sidebar keeps this component mounted
 * when the team switches reservations, so forms, dialogs and answers still
 * arriving for the previous reservation must not reach the next one.
 */
const PaymentRequests: React.FC<{ reservation: Reservation }> = ({ reservation }) => (
  <ReservationPaymentRequests key={`${reservation.restaurant}/${reservation.id}`} reservation={reservation} />
);

export default PaymentRequests;
